import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Calendar,
  Flame,
  RefreshCw,
  Sparkles,
  Sword,
  Target,
  Timer,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  gamesAdminApi,
  type CheaterRow,
  type DuelMatchRow,
  type DuelStats,
  type EloDistributionPoint,
  type GameSessionStats,
  type GamesOverview,
  type UsagePoint,
} from '../../api/games';
import { InfoHint, TabIntro } from './controls';
import SparringTab from './SparringTab';
import LimitsTab from './LimitsTab';

interface GamesPageProps {
  isDark: boolean;
}

type SubTab = 'overview' | 'usage' | 'cheaters' | 'duels' | 'sparring' | 'limits';
type UsageGame = 'daily' | 'time_attack' | 'survival';
type CheaterGame = 'time_attack' | 'survival';

export default function GamesPage({ isDark }: GamesPageProps) {
  const [tab, setTab] = useState<SubTab>('overview');

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#111111]',
            )}
          >
            Игры
          </h1>
          <p
            className={cn(
              'text-sm mt-1',
              isDark ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            Вызов дня · Тайм-атака · Выживание · Дуэль со спаррингом — все режимы работают.
          </p>
        </div>

        <div
          className={cn(
            'flex rounded-xl p-1 gap-1 border',
            isDark
              ? 'bg-slate-900 border-slate-700'
              : 'bg-white border-slate-200',
          )}
        >
          {(
            [
              { id: 'overview', label: 'Обзор' },
              { id: 'usage', label: 'Активность' },
              { id: 'duels', label: 'Дуэли' },
              { id: 'sparring', label: 'Спарринг' },
              { id: 'limits', label: 'Лимиты' },
              { id: 'cheaters', label: 'Подозрительные' },
            ] as const
          ).map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition',
                tab === item.id
                  ? 'bg-[#C9A227] text-white shadow'
                  : isDark
                    ? 'text-slate-300 hover:bg-slate-800'
                    : 'text-slate-700 hover:bg-slate-100',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' && <OverviewTab isDark={isDark} />}
      {tab === 'usage' && <UsageTab isDark={isDark} />}
      {tab === 'duels' && <DuelsTab isDark={isDark} />}
      {tab === 'sparring' && <SparringTab isDark={isDark} />}
      {tab === 'limits' && <LimitsTab isDark={isDark} />}
      {tab === 'cheaters' && <CheatersTab isDark={isDark} />}
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────

function OverviewTab({ isDark }: { isDark: boolean }) {
  const [data, setData] = useState<GamesOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // По умолчанию метрики дуэлей считают только живые матчи — иначе рост PvP
  // не отличить от накрутки спарринг-партнёрами. См. ADR 0018.
  const [includeSparring, setIncludeSparring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await gamesAdminApi.overview(includeSparring);
      setData(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  }, [includeSparring]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-center gap-3">
        <label
          className={cn(
            'flex items-center gap-2 text-sm cursor-pointer select-none',
            isDark ? 'text-slate-300' : 'text-slate-600',
          )}
        >
          <input
            type="checkbox"
            checked={includeSparring}
            onChange={(e) => setIncludeSparring(e.target.checked)}
            className="accent-[#C9A227]"
          />
          Считать дуэли со спаррингом
          <InfoHint
            isDark={isDark}
            text="По умолчанию метрики дуэлей показывают только живой PvP. Включи, чтобы увидеть общий объём вместе с матчами против спарринг-партнёров — но помни, что рост этой цифры не означает роста реального сообщества."
          />
        </label>
        <RefreshButton onClick={load} isDark={isDark} />
      </div>

      {error && <ErrorBanner isDark={isDark} message={error} />}

      <TabIntro isDark={isDark}>
        Сводка по четырём играм. <b>Попытка</b> — один сыгранный заход,{' '}
        <b>DAU / WAU</b> — уникальные игроки за сутки и за неделю: один человек
        считается один раз, сколько бы раз ни играл. Сутки везде считаются по
        UTC и начинаются в 05:00 по Ташкенту. Наведи курсор на ⓘ у любой цифры,
        чтобы узнать, как она собирается.
      </TabIntro>

      <Section
        title="Ежедневный вызов"
        icon={<Sparkles className="w-5 h-5" />}
        isDark={isDark}
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard isDark={isDark} icon={<Activity className="w-5 h-5" />} label="Сегодня" value={loading ? '—' : String(data?.daily.todayAttempts ?? 0)} accent="amber" hint="Сколько ответов на вопрос дня получено за сегодняшние UTC-сутки." />
          <KpiCard isDark={isDark} icon={<Users className="w-5 h-5" />} label="DAU" value={loading ? '—' : String(data?.daily.dau ?? 0)} accent="emerald" hint="Уникальных игроков в вызове дня за сегодня. Повторные заходы одного человека не удваивают цифру." />
          <KpiCard isDark={isDark} icon={<Users className="w-5 h-5" />} label="WAU" value={loading ? '—' : String(data?.daily.wau ?? 0)} accent="emerald" hint="Уникальных игроков в вызове дня за последние 7 дней." />
          <KpiCard isDark={isDark} icon={<TrendingUp className="w-5 h-5" />} label="Попыток за неделю" value={loading ? '—' : String(data?.daily.weekAttempts ?? 0)} accent="sky" hint="Ответов на вопрос дня за 7 дней. Один игрок даёт по одному ответу в день, так что число близко к сумме дневных." />
          <KpiCard isDark={isDark} icon={<Calendar className="w-5 h-5" />} label="Попыток за месяц" value={loading ? '—' : String(data?.daily.monthAttempts ?? 0)} accent="sky" hint="Ответов на вопрос дня за 30 дней." />
          <KpiCard isDark={isDark} icon={<Activity className="w-5 h-5" />} label="Всего" value={loading ? '—' : String(data?.daily.totalAttempts ?? 0)} accent="slate" hint="Ответов на вопрос дня за всё время существования игры." />
          <KpiCard isDark={isDark} icon={<Flame className="w-5 h-5" />} label="Игроков со страйком" value={loading ? '—' : String(data?.daily.playersWithStreak ?? 0)} accent="amber" hint="У скольких игроков серия сейчас не прервана. Серия рвётся, если пропустить день (премиум может заморозить её или доиграть задним числом)." />
          <KpiCard isDark={isDark} icon={<Trophy className="w-5 h-5" />} label="Лучший страйк" value={loading ? '—' : String(data?.daily.bestStreak ?? 0)} accent="amber" hint="Самая длинная серия за всю историю среди всех игроков — рекорд, а не текущее состояние." />
        </div>
      </Section>

      <SessionGameSection
        title="Тайм-атака"
        icon={<Timer className="w-5 h-5" />}
        stats={data?.time_attack ?? null}
        loading={loading}
        isDark={isDark}
        sectionHint="Забег на минуту: игрок отвечает на как можно больше вопросов за 60 секунд. Счёт — число верных ответов."
        scoreHint="Лучший результат одного забега за всю историю игры."
      />

      <SessionGameSection
        title="Выживание"
        icon={<Target className="w-5 h-5" />}
        stats={data?.survival ?? null}
        loading={loading}
        isDark={isDark}
        sectionHint="Забег без права на ошибку: игра идёт, пока не кончатся жизни. Премиум получает один второй шанс за забег."
        scoreHint="Самая длинная серия верных ответов за всю историю игры."
      />

      <DuelOverviewSection
        stats={data?.duel ?? null}
        loading={loading}
        isDark={isDark}
      />
    </div>
  );
}

function DuelOverviewSection({
  stats,
  loading,
  isDark,
}: {
  stats: DuelStats | null;
  loading: boolean;
  isDark: boolean;
}) {
  return (
    <Section
      title="Дуэль"
      icon={<Sword className="w-5 h-5" />}
      isDark={isDark}
      hint="Матч 1-на-1 на десяти общих вопросах. Побеждает тот, у кого больше верных; при равенстве — кто ответил быстрее. По умолчанию здесь считаются только матчи между живыми людьми."
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard isDark={isDark} icon={<Activity className="w-5 h-5" />} label="Сегодня" value={loading ? '—' : String(stats?.todayCompleted ?? 0)} accent="amber" hint="Матчей, доигранных до конца за сегодня. Матч считается завершённым, когда сыграли обе стороны." />
        <KpiCard isDark={isDark} icon={<Users className="w-5 h-5" />} label="DAU" value={loading ? '—' : String(stats?.dau ?? 0)} accent="emerald" hint="Уникальных игроков за сегодня." />
        <KpiCard isDark={isDark} icon={<TrendingUp className="w-5 h-5" />} label="Неделя" value={loading ? '—' : String(stats?.weekCompleted ?? 0)} accent="sky" hint="Завершённых матчей за 7 дней." />
        <KpiCard isDark={isDark} icon={<Calendar className="w-5 h-5" />} label="Месяц" value={loading ? '—' : String(stats?.monthCompleted ?? 0)} accent="sky" hint="Завершённых матчей за 30 дней." />
        <KpiCard isDark={isDark} icon={<Trophy className="w-5 h-5" />} label="Всего завершено" value={loading ? '—' : String(stats?.totalCompleted ?? 0)} accent="slate" hint="Завершённых матчей за всё время." />
        <KpiCard isDark={isDark} icon={<Sword className="w-5 h-5" />} label="Активных матчей" value={loading ? '—' : String(stats?.activeMatches ?? 0)} accent="amber" hint="Матчей, где соперник найден, но кто-то ещё не доиграл. Если игрок бросит матч, он протухнет и уйдёт в архив автоматически." />
        <KpiCard isDark={isDark} icon={<Sword className="w-5 h-5" />} label="Ожидают" value={loading ? '—' : String(stats?.waitingMatches ?? 0)} accent="slate" hint="Матчей, ждущих соперника. После включения спарринга это число должно держаться около нуля: партнёр подсаживается за секунды, а зависшие добивает крон." />
      </div>
    </Section>
  );
}

function DuelsTab({ isDark }: { isDark: boolean }) {
  const [matches, setMatches] = useState<DuelMatchRow[]>([]);
  const [elo, setElo] = useState<EloDistributionPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, e] = await Promise.all([
        gamesAdminApi.duelsActive(100),
        gamesAdminApi.eloDistribution(),
      ]);
      setMatches(m);
      setElo(e);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const maxBucket = Math.max(1, ...elo.map((e) => e.count));

  return (
    <div className="space-y-6">
      <TabIntro isDark={isDark}>
        Здесь видно, как игроки распределены по силе и что происходит с матчами
        прямо сейчас. <b>ELO</b> — шахматный рейтинг: старт 1000, победа над
        сильным даёт больше очков, чем над слабым. Ранги: до 1100 бронза, 1100
        серебро, 1300 золото, 1500 платина, 1750 алмаз.
      </TabIntro>

      <div className="flex justify-end">
        <RefreshButton onClick={load} isDark={isDark} />
      </div>
      {error && <ErrorBanner isDark={isDark} message={error} />}

      {/* ELO distribution */}
      <div
        className={cn(
          'rounded-2xl p-5 border',
          isDark ? 'bg-[#111111] border-slate-700' : 'bg-white border-slate-200 shadow-sm',
        )}
      >
        <h3 className={cn('font-semibold tracking-tight mb-4', isDark ? 'text-white' : 'text-[#111111]')}>
          Распределение ELO (корзины по 50 очков)
          <InfoHint
            isDark={isDark}
            text="Каждый столбик — сколько игроков имеют рейтинг в этом диапазоне. Здоровая картина — горка вокруг 1000. Если весь график уползает вправо, значит побеждать стало слишком легко: проверь точность спарринг-профилей."
          />
        </h3>
        {loading ? (
          <div className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Загрузка…</div>
        ) : elo.length === 0 ? (
          <div className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Нет данных — никто ещё не играл в дуэль
          </div>
        ) : (
          <div className="flex items-end gap-1 h-48">
            {elo.map((b) => {
              const h = Math.max(2, (b.count / maxBucket) * 100);
              return (
                <div key={b.bucket} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${b.bucket}-${b.bucket + 49}: ${b.count}`}>
                  <div
                    className={cn(
                      'w-full rounded-t-md transition-all',
                      isDark ? 'bg-sky-500' : 'bg-sky-400',
                    )}
                    style={{ height: `${h}%` }}
                  />
                  <span className={cn('text-[9px] font-mono', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    {b.bucket}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Active / waiting matches */}
      <div
        className={cn(
          'rounded-2xl border overflow-hidden',
          isDark ? 'bg-[#111111] border-slate-700' : 'bg-white border-slate-200 shadow-sm',
        )}
      >
        <div className={cn('px-5 py-4 border-b', isDark ? 'border-slate-700' : 'border-slate-200')}>
          <h3 className={cn('font-semibold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>
            Активные и ожидающие матчи
            <InfoHint
              isDark={isDark}
              text="«Ожидает» — игрок ищет соперника и ещё никого не нашёл. «Активен» — соперник есть, но матч не доигран. Игрок A всегда создатель матча, игрок B — тот, кто подключился; спарринг-партнёр всегда оказывается игроком B."
            />
          </h3>
        </div>
        {loading ? (
          <div className={cn('p-5 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Загрузка…</div>
        ) : matches.length === 0 ? (
          <div className={cn('p-5 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Нет активных матчей
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={cn('text-left', isDark ? 'text-slate-400 bg-slate-900/50' : 'text-slate-600 bg-slate-50')}>
                  <th className="px-4 py-3 font-semibold">Статус</th>
                  <th className="px-4 py-3 font-semibold">
                    Игрок A
                    <InfoHint isDark={isDark} text="Тот, кто нажал «Найти соперника» и создал матч." />
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Игрок B
                    <InfoHint isDark={isDark} text="Подключившийся соперник: живой игрок или спарринг-партнёр. Пусто — соперника ещё нет." />
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Тема
                    <InfoHint isDark={isDark} text="Дуэль по конкретной теме ПДД — премиум-режим. Пусто означает вопросы из всего банка." />
                  </th>
                  <th className="px-4 py-3 font-semibold">Создан</th>
                  <th className="px-4 py-3 font-semibold">Истекает</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((m) => (
                  <tr
                    key={m.id}
                    className={cn(
                      'border-t',
                      isDark ? 'border-slate-800 text-slate-300' : 'border-slate-100 text-slate-700',
                    )}
                  >
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-block px-2 py-0.5 rounded text-xs font-semibold',
                          m.status === 'active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700',
                        )}
                      >
                        {m.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{m.playerA.substring(0, 8)}…</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {m.playerB ? `${m.playerB.substring(0, 8)}…` : '—'}
                    </td>
                    <td className="px-4 py-3">{m.topicId ?? '—'}</td>
                    <td className="px-4 py-3 text-xs">{new Date(m.createdAt).toLocaleString('ru-RU')}</td>
                    <td className="px-4 py-3 text-xs">
                      {m.expiresAt ? new Date(m.expiresAt).toLocaleString('ru-RU') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SessionGameSection({
  title,
  icon,
  stats,
  loading,
  isDark,
  sectionHint,
  scoreHint,
}: {
  title: string;
  icon: React.ReactNode;
  stats: GameSessionStats | null;
  loading: boolean;
  isDark: boolean;
  sectionHint: string;
  scoreHint: string;
}) {
  return (
    <Section title={title} icon={icon} isDark={isDark} hint={sectionHint}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard isDark={isDark} icon={<Activity className="w-5 h-5" />} label="Сегодня" value={loading ? '—' : String(stats?.todaySessions ?? 0)} accent="amber" hint="Завершённых заходов за сегодня. Брошенные и не отправленные на сервер не считаются." />
        <KpiCard isDark={isDark} icon={<Users className="w-5 h-5" />} label="DAU" value={loading ? '—' : String(stats?.dau ?? 0)} accent="emerald" hint="Уникальных живых игроков в дуэлях за сегодня. Спарринг-партнёры сюда не попадают никогда, даже с включённым переключателем." />
        <KpiCard isDark={isDark} icon={<Users className="w-5 h-5" />} label="WAU" value={loading ? '—' : String(stats?.wau ?? 0)} accent="emerald" hint="Уникальных игроков за последние 7 дней." />
        <KpiCard isDark={isDark} icon={<TrendingUp className="w-5 h-5" />} label="Неделя" value={loading ? '—' : String(stats?.weekSessions ?? 0)} accent="sky" hint="Завершённых заходов за 7 дней." />
        <KpiCard isDark={isDark} icon={<Calendar className="w-5 h-5" />} label="Месяц" value={loading ? '—' : String(stats?.monthSessions ?? 0)} accent="sky" hint="Завершённых заходов за 30 дней." />
        <KpiCard isDark={isDark} icon={<Activity className="w-5 h-5" />} label="Всего" value={loading ? '—' : String(stats?.totalSessions ?? 0)} accent="slate" hint="Завершённых заходов за всё время." />
        <KpiCard isDark={isDark} icon={<Trophy className="w-5 h-5" />} label="Лучший счёт" value={loading ? '—' : String(stats?.bestScoreAll ?? 0)} accent="amber" hint={scoreHint} />
        <KpiCard isDark={isDark} icon={<AlertTriangle className="w-5 h-5" />} label="Помечено" value={loading ? '—' : String(stats?.flaggedCount ?? 0)} accent={(stats?.flaggedCount ?? 0) > 0 ? 'red' : 'slate'} hint="Сколько заходов сервер счёл невозможными — например, слишком быстрые ответы или подделанное время. Они не попадают в лидерборд; разбор на вкладке «Подозрительные»." />
      </div>
    </Section>
  );
}

// ─── Usage chart tab ──────────────────────────────────────────────────────

function UsageTab({ isDark }: { isDark: boolean }) {
  const [game, setGame] = useState<UsageGame>('daily');
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<UsagePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await gamesAdminApi.usage(game, days);
      setData(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [game, days]);

  const max = Math.max(1, ...data.map((d) => d.count));
  const total = data.reduce((s, d) => s + d.count, 0);

  return (
    <div className="space-y-6">
      <TabIntro isDark={isDark}>
        График показывает, сколько заходов в игру было в каждый день выбранного
        периода. Это <b>объём игры</b>, а не число игроков: один человек за день
        мог сыграть несколько раз. Провалы обычно означают либо праздники, либо
        поломку — сверяй их с датами релизов.
      </TabIntro>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2">
          {(['daily', 'time_attack', 'survival'] as UsageGame[]).map((g) => (
            <button
              key={g}
              onClick={() => setGame(g)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-semibold transition',
                game === g
                  ? 'bg-[#C9A227] text-white'
                  : isDark
                    ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100',
              )}
            >
              {g === 'daily' ? 'Daily' : g === 'time_attack' ? 'Time Attack' : 'Survival'}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          {[7, 14, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-semibold transition',
                days === n
                  ? 'bg-[#C9A227] text-white'
                  : isDark
                    ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100',
              )}
            >
              {n}d
            </button>
          ))}
          <RefreshButton onClick={load} isDark={isDark} />
        </div>
      </div>

      {error && <ErrorBanner isDark={isDark} message={error} />}

      <div
        className={cn(
          'rounded-2xl p-5 border',
          isDark ? 'bg-[#111111] border-slate-700' : 'bg-white border-slate-200 shadow-sm',
        )}
      >
        <div className="flex items-baseline justify-between mb-4">
          <h3 className={cn('font-semibold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>
            Sessions per day · last {days} days
          </h3>
          <div className={cn('text-sm font-semibold', isDark ? 'text-slate-300' : 'text-slate-600')}>
            Total: <span className={cn(isDark ? 'text-white' : 'text-[#111111]')}>{total}</span>
          </div>
        </div>

        {loading ? (
          <div className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Загрузка…</div>
        ) : data.length === 0 ? (
          <div className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Нет данных</div>
        ) : (
          <div className="flex items-end gap-1 h-48">
            {data.map((d) => {
              const h = Math.max(2, (d.count / max) * 100);
              return (
                <div
                  key={d.date}
                  className="flex-1 group flex flex-col items-center justify-end relative"
                  title={`${d.date}: ${d.count}`}
                >
                  <div
                    className={cn(
                      'w-full rounded-t-md transition-all',
                      isDark ? 'bg-amber-500' : 'bg-amber-400',
                    )}
                    style={{ height: `${h}%` }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Cheaters tab ─────────────────────────────────────────────────────────

function CheatersTab({ isDark }: { isDark: boolean }) {
  const [game, setGame] = useState<CheaterGame>('time_attack');
  const [data, setData] = useState<CheaterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await gamesAdminApi.cheaters(game, 100);
      setData(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [game]);

  return (
    <div className="space-y-6">
      <TabIntro isDark={isDark} tone="warning">
        Заходы, которые сервер счёл невозможными: например, ответы быстрее, чем
        человек успевает прочитать вопрос, или подделанное время игры. Такие
        сессии получают статус <b>flagged</b> — результат не идёт в лидерборд и
        в рекорды, но сам игрок не блокируется. Пустой список — это норма и
        хороший знак.
      </TabIntro>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2">
          {(['time_attack', 'survival'] as CheaterGame[]).map((g) => (
            <button
              key={g}
              onClick={() => setGame(g)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-semibold transition',
                game === g
                  ? 'bg-[#C9A227] text-white'
                  : isDark
                    ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100',
              )}
            >
              {g === 'time_attack' ? 'Time Attack' : 'Survival'}
            </button>
          ))}
        </div>
        <RefreshButton onClick={load} isDark={isDark} />
      </div>

      {error && <ErrorBanner isDark={isDark} message={error} />}

      <div
        className={cn(
          'rounded-2xl border overflow-hidden',
          isDark ? 'bg-[#111111] border-slate-700' : 'bg-white border-slate-200 shadow-sm',
        )}
      >
        <div
          className={cn(
            'px-5 py-4 border-b flex items-center gap-2',
            isDark ? 'border-slate-700' : 'border-slate-200',
          )}
        >
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          <h3 className={cn('font-semibold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>
            Помеченные сессии
          </h3>
        </div>

        {loading ? (
          <div className={cn('p-5 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Загрузка…</div>
        ) : data.length === 0 ? (
          <div className={cn('p-5 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Нет помеченных сессий — всё чисто!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={cn('text-left', isDark ? 'text-slate-400 bg-slate-900/50' : 'text-slate-600 bg-slate-50')}>
                  <th className="px-4 py-3 font-semibold">ID юзера</th>
                  <th className="px-4 py-3 font-semibold">Счёт</th>
                  {game === 'time_attack' && <th className="px-4 py-3 font-semibold">Длительность</th>}
                  {game === 'survival' && <th className="px-4 py-3 font-semibold">Реврайвы</th>}
                  <th className="px-4 py-3 font-semibold">Завершено</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-t',
                      isDark ? 'border-slate-800 text-slate-300' : 'border-slate-100 text-slate-700',
                    )}
                  >
                    <td className="px-4 py-3 font-mono text-xs">{row.user_id.substring(0, 8)}…</td>
                    <td className="px-4 py-3 font-semibold">{row.score}</td>
                    {game === 'time_attack' && (
                      <td className="px-4 py-3">{row.duration_ms ?? '—'} ms</td>
                    )}
                    {game === 'survival' && (
                      <td className="px-4 py-3">{row.used_revive ? 'да' : 'нет'}</td>
                    )}
                    <td className="px-4 py-3 text-xs">
                      {row.finished_at
                        ? new Date(row.finished_at).toLocaleString('ru-RU')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared atoms ─────────────────────────────────────────────────────────

function Section({
  title,
  icon,
  children,
  isDark,
  locked,
  hint,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isDark: boolean;
  locked?: boolean;
  hint?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl p-5 border',
        isDark ? 'bg-[#111111] border-slate-700' : 'bg-white border-slate-200 shadow-sm',
        locked && 'opacity-60',
      )}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={cn(
            'w-9 h-9 rounded-xl flex items-center justify-center',
            isDark ? 'bg-slate-800 text-amber-400' : 'bg-amber-50 text-amber-600',
          )}
        >
          {icon}
        </div>
        <h2
          className={cn(
            'font-semibold tracking-tight',
            isDark ? 'text-white' : 'text-[#111111]',
          )}
        >
          {title}
          {hint && <InfoHint isDark={isDark} text={hint} />}
        </h2>
      </div>
      {children}
    </div>
  );
}

function KpiCard({
  isDark,
  icon,
  label,
  value,
  accent,
  hint,
}: {
  isDark: boolean;
  icon: React.ReactNode;
  label: string;
  value: string;
  accent: 'amber' | 'emerald' | 'sky' | 'slate' | 'red';
  hint?: string;
}) {
  const accentBg: Record<typeof accent, string> = {
    amber: isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600',
    emerald: isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600',
    sky: isDark ? 'bg-sky-500/10 text-sky-400' : 'bg-sky-50 text-sky-600',
    slate: isDark ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-600',
    red: isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600',
  };

  return (
    <div
      className={cn(
        'rounded-xl p-4 flex items-start justify-between gap-3 border',
        isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
      )}
    >
      <div className="space-y-2 min-w-0">
        <p
          className={cn(
            'text-xs font-medium uppercase tracking-wider',
            isDark ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          {label}
          {hint && <InfoHint isDark={isDark} text={hint} />}
        </p>
        <p
          className={cn(
            'text-2xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#111111]',
          )}
        >
          {value}
        </p>
      </div>
      <div
        className={cn(
          'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
          accentBg[accent],
        )}
      >
        {icon}
      </div>
    </div>
  );
}

function RefreshButton({ onClick, isDark }: { onClick: () => void; isDark: boolean }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition',
        isDark
          ? 'bg-slate-800 text-white hover:bg-slate-700'
          : 'bg-white text-slate-800 hover:bg-slate-100 shadow-sm border border-slate-200',
      )}
    >
      <RefreshCw className="w-4 h-4" />
      Обновить
    </button>
  );
}

function ErrorBanner({ message, isDark }: { message: string; isDark: boolean }) {
  return (
    <div
      className={cn(
        'rounded-xl p-4 text-sm font-medium',
        isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700',
      )}
    >
      {message}
    </div>
  );
}
